/**
 * UserInterestsContext
 *
 * Stores the user's safari category interests purely in localStorage.
 * No authentication or account is required — interests persist across
 * browser sessions on the same device, or reset when the user clears
 * site data.
 *
 * Storage key: sighttrail_interests_v1
 * Shape: { interests: string[], seenAt: number }
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

const STORAGE_KEY = 'sighttrail_interests_v1';

export interface UserInterestsData {
  /** Category names the user selected (e.g. ["Luxury", "Adventure"]) */
  interests: string[];
  /** Unix timestamp of when the picker was completed */
  seenAt: number;
}

interface UserInterestsContextType {
  /** Has the user completed or skipped the interest picker? */
  hasSeenPicker: boolean;
  /** The interests they selected (empty array if skipped) */
  interests: string[];
  /** Call this to save the user's selections and dismiss the picker */
  saveInterests: (selected: string[]) => void;
  /** Call this if the user clicks "Skip" — marks picker as seen with empty interests */
  skipPicker: () => void;
  /** Reset interests (e.g. from a profile settings page) */
  resetInterests: () => void;
}

const UserInterestsContext = createContext<UserInterestsContextType | undefined>(undefined);

export const UserInterestsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasSeenPicker, setHasSeenPicker] = useState(false);
  const [interests, setInterests] = useState<string[]>([]);

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed: UserInterestsData = JSON.parse(raw);
        setInterests(parsed.interests || []);
        setHasSeenPicker(true);
      }
    } catch {
      // Corrupted storage — treat as first visit
    }
  }, []);

  const persist = (selected: string[]) => {
    const data: UserInterestsData = {
      interests: selected,
      seenAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  };

  const saveInterests = (selected: string[]) => {
    persist(selected);
    setInterests(selected);
    setHasSeenPicker(true);
  };

  const skipPicker = () => {
    persist([]);
    setInterests([]);
    setHasSeenPicker(true);
  };

  const resetInterests = () => {
    localStorage.removeItem(STORAGE_KEY);
    setInterests([]);
    setHasSeenPicker(false);
  };

  return (
    <UserInterestsContext.Provider value={{ hasSeenPicker, interests, saveInterests, skipPicker, resetInterests }}>
      {children}
    </UserInterestsContext.Provider>
  );
};

export const useUserInterests = () => {
  const ctx = useContext(UserInterestsContext);
  if (!ctx) throw new Error('useUserInterests must be used within UserInterestsProvider');
  return ctx;
};
